import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider, linkWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../config/firebase';
import './AccountManager.css';

interface AccountManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserProfile {
  displayName?: string;
  email?: string;
  photoURL?: string;
  location?: string;
  phone?: string;
  bio?: string;
  company?: {
    name?: string;
    registrationNumber?: string;
    address?: string;
    website?: string;
    logoURL?: string;
  };
}

const AccountManager: React.FC<AccountManagerProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'company' | 'security'>('profile');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Profile form data
  const [profileData, setProfileData] = useState<UserProfile>({
    displayName: '',
    location: '',
    phone: '',
    bio: '',
    company: {
      name: '',
      registrationNumber: '',
      address: '',
      website: ''
    }
  });

  // Security form data
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [companyLogo, setCompanyLogo] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string>('');
  const [companyLogoPreview, setCompanyLogoPreview] = useState<string>('');

  // Load user data on component mount
  useEffect(() => {
    if (user && isOpen) {
      loadUserData();
    }
  }, [user, isOpen]);

  const loadUserData = async () => {
    if (!user || !auth.currentUser) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      setProfileData({
        displayName: auth.currentUser.displayName || user.name || '',
        email: user.email || '',
        photoURL: auth.currentUser.photoURL || user.profileImageUrl || '',
        location: userData?.location || '',
        phone: userData?.phone || '',
        bio: userData?.bio || '',
        company: {
          name: userData?.company?.name || '',
          registrationNumber: userData?.company?.registrationNumber || '',
          address: userData?.company?.address || '',
          website: userData?.company?.website || '',
          logoURL: userData?.company?.logoUrl || ''
        }
      });

      setProfileImagePreview(auth.currentUser.photoURL || user.profileImageUrl || '');
      setCompanyLogoPreview(userData?.company?.logoUrl || '');
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const generateAvatarUrl = (name: string) => {
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&size=200&background=E6A825&color=ffffff&format=png&rounded=true&font-size=0.5&bold=true`;
  };

  const handleImageUpload = (file: File, type: 'profile' | 'company') => {
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 5MB' });
      return;
    }

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    if (type === 'profile') {
      setProfileImage(file);
      setProfileImagePreview(URL.createObjectURL(file));
    } else {
      setCompanyLogo(file);
      setCompanyLogoPreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async (file: File, type: 'profile' | 'company'): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 15);
    const fileName = `${type}-${user.uid}-${timestamp}-${randomStr}.${file.name.split('.').pop()}`;
    const storageRef = ref(storage, `users/${user.uid}/${fileName}`);

    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleProfileSave = async () => {
    if (!user) return;

    setLoading(true);
    setMessage(null);

    try {
      let photoURL = profileData.photoURL;

      // Upload profile image if changed
      if (profileImage) {
        setUploading(true);
        photoURL = await uploadImage(profileImage, 'profile');
        setUploading(false);
      }

      // Upload company logo if changed
      let logoURL = profileData.company?.logoURL;
      if (companyLogo) {
        setUploading(true);
        logoURL = await uploadImage(companyLogo, 'company');
        setUploading(false);
      }

      // Update Firebase Auth profile
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: profileData.displayName,
          photoURL: photoURL
        });
      }

      // Update Firestore user document
      await updateDoc(doc(db, 'users', user.uid), {
        name: profileData.displayName,
        location: profileData.location,
        phone: profileData.phone,
        bio: profileData.bio,
        profileImageUrl: photoURL,
        company: {
          ...profileData.company,
          logoUrl: logoURL
        },
        updatedAt: new Date()
      });

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setProfileImage(null);
      setCompanyLogo(null);
      
      // Reload user data to reflect changes
      setTimeout(loadUserData, 1000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!user || !auth.currentUser) return;

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email!, passwordData.currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Update password
      await updatePassword(auth.currentUser, passwordData.newPassword);

      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update password' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLink = async () => {
    if (!auth.currentUser) return;

    setLoading(true);
    setMessage(null);

    try {
      const provider = new GoogleAuthProvider();
      await linkWithPopup(auth.currentUser, provider);
      setMessage({ type: 'success', text: 'Google account linked successfully!' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to link Google account' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="account-modal-overlay">
      <div className="account-modal-content">
        <div className="account-modal-header">
          <div className="account-modal-title">
            <span className="account-icon">👤</span>
            <h2>My Account</h2>
          </div>
          <button className="account-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="account-tabs">
          <button 
            className={`account-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <span className="tab-icon">👤</span>
            Profile
          </button>
          <button 
            className={`account-tab ${activeTab === 'company' ? 'active' : ''}`}
            onClick={() => setActiveTab('company')}
          >
            <span className="tab-icon">🏢</span>
            Company
          </button>
          <button 
            className={`account-tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <span className="tab-icon">🔒</span>
            Security
          </button>
        </div>

        <div className="account-modal-body">
          {message && (
            <div className={`account-message ${message.type}`}>
              {message.text}
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="account-tab-content">
              <h3>Profile Information</h3>
              
              <div className="profile-image-section">
                <div className="profile-image-container">
                  <img 
                    src={profileImagePreview || generateAvatarUrl(profileData.displayName || 'User')} 
                    alt="Profile" 
                    className="profile-image-preview"
                  />
                </div>
                <div className="profile-image-controls">
                  <label className="upload-btn">
                    📷 Change Photo
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0], 'profile')}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={profileData.displayName || ''}
                    onChange={(e) => setProfileData({...profileData, displayName: e.target.value})}
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={profileData.email || ''}
                    disabled
                    placeholder="Email cannot be changed"
                    className="disabled-input"
                  />
                  <small>Email cannot be changed. Contact support if needed.</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Location (Postcode area)</label>
                  <input
                    type="text"
                    value={profileData.location || ''}
                    onChange={(e) => setProfileData({...profileData, location: e.target.value})}
                    placeholder="e.g., SW1A 1AA"
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={profileData.phone || ''}
                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={profileData.bio || ''}
                  onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                  placeholder="Tell us about yourself..."
                  rows={4}
                />
              </div>
            </div>
          )}

          {activeTab === 'company' && (
            <div className="account-tab-content">
              <h3>Company Details</h3>
              <p className="tab-description">Optional - for commercial beekeepers and businesses</p>
              
              <div className="company-logo-section">
                <div className="company-logo-container">
                  {companyLogoPreview ? (
                    <img src={companyLogoPreview} alt="Company Logo" className="company-logo-preview" />
                  ) : (
                    <div className="no-logo-placeholder">
                      <span>🏢</span>
                      <span>No logo uploaded</span>
                    </div>
                  )}
                </div>
                <div className="company-logo-controls">
                  <label className="upload-btn">
                    📁 Upload Logo
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0], 'company')}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Company Name</label>
                  <input
                    type="text"
                    value={profileData.company?.name || ''}
                    onChange={(e) => setProfileData({
                      ...profileData, 
                      company: {...profileData.company, name: e.target.value}
                    })}
                    placeholder="Enter company name"
                  />
                </div>
                <div className="form-group">
                  <label>Registration Number</label>
                  <input
                    type="text"
                    value={profileData.company?.registrationNumber || ''}
                    onChange={(e) => setProfileData({
                      ...profileData, 
                      company: {...profileData.company, registrationNumber: e.target.value}
                    })}
                    placeholder="Company registration number"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <textarea
                  value={profileData.company?.address || ''}
                  onChange={(e) => setProfileData({
                    ...profileData, 
                    company: {...profileData.company, address: e.target.value}
                  })}
                  placeholder="Company address"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={profileData.company?.website || ''}
                  onChange={(e) => setProfileData({
                    ...profileData, 
                    company: {...profileData.company, website: e.target.value}
                  })}
                  placeholder="https://yourwebsite.com"
                />
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="account-tab-content">
              <h3>Security & Authentication</h3>
              
              <div className="security-section">
                <div className="security-item">
                  <div className="security-item-content">
                    <h4>🔗 Google Single Sign-On (SSO)</h4>
                    <p>Link your Google account for easier login and enhanced security.</p>
                  </div>
                  <button className="link-google-btn" onClick={handleGoogleLink} disabled={loading}>
                    🔗 Link Google Account
                  </button>
                </div>

                <div className="security-item">
                  <div className="security-item-content">
                    <h4>🔑 Password</h4>
                    <p>Change your account password for enhanced security.</p>
                  </div>
                  <div className="password-form">
                    <div className="form-group">
                      <label>Current Password</label>
                      <input
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        placeholder="Enter current password"
                      />
                    </div>
                    <div className="form-group">
                      <label>New Password</label>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        placeholder="Enter new password"
                      />
                    </div>
                    <div className="form-group">
                      <label>Confirm New Password</label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <button 
                      className="change-password-btn" 
                      onClick={handlePasswordChange}
                      disabled={loading || !passwordData.currentPassword || !passwordData.newPassword}
                    >
                      🔒 Change Password
                    </button>
                  </div>
                </div>

                <div className="security-item">
                  <div className="security-item-content">
                    <h4>📱 Two-Factor Authentication</h4>
                    <p>Add an extra layer of security to your account.</p>
                    <small>Coming soon...</small>
                  </div>
                  <button className="enable-2fa-btn" disabled>
                    🛡️ Enable 2FA (Coming Soon)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="account-modal-footer">
          {activeTab !== 'security' && (
            <>
              <button className="btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleProfileSave}
                disabled={loading || uploading}
              >
                {uploading ? 'Uploading...' : loading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountManager;